import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../auth/msalConfig';
import { fetchSurveys, fetchSurveyQuestions, createSurvey, type SurveyQuestionItem } from '../../api/surveyApi';

const DEFAULT_INVITE =
  "Hi [First Name], we'd love your feedback on your experience at Gridworks. " +
  "Please take a few minutes to complete our [Survey Name] — your responses are completely anonymous. " +
  "Click below to get started.";

const DEFAULT_LANDING =
  "Welcome to the [Survey Name]. Your honest feedback helps us make Gridworks a better place to work. " +
  "This survey takes approximately 5–10 minutes to complete. " +
  "Your responses are fully anonymous — we never store your identity with your answers.";

function defaultTitle(): string {
  return `Engagement Survey — ${new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}`;
}

function defaultCloseDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
}

export function CreateSurvey() {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();

  const [title, setTitle] = useState(defaultTitle());
  const [closeDate, setCloseDate] = useState(defaultCloseDate());
  const [inviteMsg, setInviteMsg] = useState(DEFAULT_INVITE);
  const [landingMsg, setLandingMsg] = useState(DEFAULT_LANDING);

  const [templateSurveyId, setTemplateSurveyId] = useState<string | null>(null);
  const [templateTitle, setTemplateTitle] = useState<string>('');
  const [questions, setQuestions] = useState<SurveyQuestionItem[]>([]);
  const [loadingQ, setLoadingQ] = useState(true);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getToken = async () => {
    const r = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
    return r.accessToken;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const surveys = await fetchSurveys(token);
        // Pick the most recent survey that has questions (imported ones do)
        const template = surveys.find(s => s.isImported) ?? surveys[0];
        if (!template || cancelled) { setLoadingQ(false); return; }
        setTemplateSurveyId(template.id);
        setTemplateTitle(template.importLabel ?? template.title);
        const qs = await fetchSurveyQuestions(template.id, token);
        if (!cancelled) setQuestions(qs);
      } catch {
        // no template found — user can still create without questions
      } finally {
        if (!cancelled) setLoadingQ(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!closeDate) { setError('Please set a close date.'); return; }
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      await createSurvey({
        title,
        invitationMessage: inviteMsg,
        landingPageMessage: landingMsg,
        closedAt: new Date(closeDate + 'T23:59:59Z').toISOString(),
        copyQuestionsFromSurveyId: templateSurveyId ?? undefined,
      }, token);
      navigate('/admin/surveys');
    } catch {
      setError('Failed to create survey. Please try again.');
      setSaving(false);
    }
  };

  const typeBadge = (t: SurveyQuestionItem['type']) => {
    const map: Record<string, string> = { Scale: '#dbeafe', MultipleChoice: '#fef9c3', FreeText: '#f3f4f6' };
    return (
      <span style={{
        fontSize: '0.7rem', fontWeight: 600, padding: '0.1rem 0.45rem',
        borderRadius: 10, background: map[t] ?? '#f3f4f6', color: '#374151',
      }}>{t}</span>
    );
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
        <button className="btn btn-ghost" style={{ minHeight: 32, padding: '0 0.75rem', fontSize: '0.85rem' }}
          onClick={() => navigate('/admin/surveys')}>← Back</button>
        <h2 style={{ margin: 0, fontSize: '1.25rem' }}>New Survey</h2>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Title */}
        <label style={labelStyle}>Survey title</label>
        <input
          className="form-control"
          value={title}
          onChange={e => setTitle(e.target.value)}
          required
          style={inputStyle}
        />

        {/* Close date */}
        <label style={labelStyle}>Close date</label>
        <input
          type="date"
          className="form-control"
          value={closeDate}
          onChange={e => setCloseDate(e.target.value)}
          required
          style={{ ...inputStyle, maxWidth: 220 }}
        />
        <p style={hintStyle}>Survey links expire at midnight UTC on this date.</p>

        {/* Invite message */}
        <label style={labelStyle}>Invitation email message</label>
        <p style={hintStyle}>Merge fields: [First Name], [Survey Name], [Close Date]</p>
        <textarea
          className="form-control"
          rows={4}
          value={inviteMsg}
          onChange={e => setInviteMsg(e.target.value)}
          style={{ ...inputStyle, resize: 'vertical' }}
        />

        {/* Landing page message */}
        <label style={labelStyle}>Survey landing page message</label>
        <p style={hintStyle}>Merge fields: [Survey Name], [Close Date]</p>
        <textarea
          className="form-control"
          rows={4}
          value={landingMsg}
          onChange={e => setLandingMsg(e.target.value)}
          style={{ ...inputStyle, resize: 'vertical' }}
        />

        {/* Questions preview */}
        <div style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
            <strong style={{ fontSize: '0.95rem' }}>Questions</strong>
            {templateTitle && (
              <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                — copied from "{templateTitle}"
              </span>
            )}
          </div>
        </div>

        {loadingQ ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>Loading questions…</p>
        ) : questions.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
            No template survey found — questions can be added after creating.
          </p>
        ) : (
          <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden', marginBottom: '1.5rem' }}>
            {questions.map((q, i) => (
              <div key={q.id} style={{
                display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.65rem 1rem',
                borderBottom: i < questions.length - 1 ? '1px solid var(--color-border)' : undefined,
                background: 'var(--color-surface)',
              }}>
                <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem', minWidth: 20, paddingTop: 2 }}>
                  {i + 1}.
                </span>
                <span style={{ flex: 1, fontSize: '0.875rem' }}>{q.text}</span>
                {typeBadge(q.type)}
              </div>
            ))}
          </div>
        )}

        {error && <p style={{ color: 'var(--color-error)', fontSize: '0.875rem', marginBottom: '1rem' }}>{error}</p>}

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Creating…' : 'Create Survey'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => navigate('/admin/surveys')}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.35rem', marginTop: '1.25rem',
};
const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', marginBottom: '0.25rem',
};
const hintStyle: React.CSSProperties = {
  fontSize: '0.775rem', color: 'var(--color-text-muted)', margin: '0 0 0.4rem',
};
